import { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '~/components/ui/dialog';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import SelectStudentsStep from '../Enrollments/wizardSteps/SelectStudentsStep';
import { CHECKLIST_KIND_WORDS } from '../Enrollments/wizardSteps/SelectKindStep';
import { CreateEnrollmentsMutation } from '../Enrollments/useCreateEnrollments';
import type { AddStudentsToFamilyWizardProps } from './types';

/**
 * AddStudentsToFamilyWizard - A simplified wizard for adding students to a family enrollment
 *
 * This wizard is used from the family detail page and has a simpler flow:
 * 1. If no existing enrollment, select enrollment kind
 * 2. Select students and their grade levels
 * 3. Submit
 */
function AddStudentsToFamilyWizard({
  open,
  onOpenChange,
  familyId,
  familyName,
  schoolYearId,
  gradeLevels,
  availableChildren,
  hasExistingEnrollment,
  existingEnrollmentKind,
  checklistEnrollmentOn = false,
}: AddStudentsToFamilyWizardProps) {
  const [step, setStep] = useState(hasExistingEnrollment ? 1 : 0);
  const [enrollmentKind, setEnrollmentKind] = useState(
    existingEnrollmentKind || 'NEW_ADMISSION'
  );
  const [selections, setSelections] = useState<any[]>([]);

  // Initialize selections when children change
  useEffect(() => {
    const initialSelections = availableChildren.map((child) => ({
      personId: child.id,
      selected: true,
      gradeLevel: '',
      enrollmentKind: enrollmentKind === 'RE_ENROLLMENT' ? 'RETURNING_STUDENT' : 'NEW_STUDENT',
    }));
    setSelections(initialSelections);
  }, [availableChildren, enrollmentKind]);

  const [createEnrollments, { loading }] = useMutation(CreateEnrollmentsMutation);

  const handleClose = (didCreate = false) => {
    onOpenChange(didCreate);
  };

  const handleNext = () => {
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    const selectedStudents = selections
      .filter(s => s.selected)
      .map(s => ({
        personId: s.personId,
        gradeLevel: s.gradeLevel || null,
        enrollmentKind: s.enrollmentKind,
      }));

    if (selectedStudents.length === 0) {
      return;
    }

    try {
      const result = await createEnrollments({
        variables: {
          input: {
            schoolYearId: schoolYearId!,
            enrollmentKind: enrollmentKind as "NEW_ADMISSION" | "RE_ENROLLMENT",
            familyIds: [familyId],
            studentSelections: selectedStudents,
          },
        },
      });

      const userErrors = result.data?.createEnrollments?.userErrors;
      if (userErrors && userErrors.length > 0) {
        console.error('Enrollment errors:', userErrors);
      } else {
        handleClose(true);
      }
    } catch (error) {
      console.error('Failed to create enrollments:', error);
    }
  };

  const selectedCount = selections.filter(s => s.selected).length;
  const canProceed = selectedCount > 0;

  // Transform children for SelectStudentsStep
  const studentsForStep = availableChildren.map((child) => ({
    id: child.id,
    firstName: child.firstName,
    lastName: child.lastName,
    familyId: familyId,
    familyName: familyName,
  }));

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {step === 0 ? 'Select Enrollment Type' : 'Add Students to Enrollment'}
          </DialogTitle>
          <DialogDescription>
            {step === 0
              ? `Choose the type of enrollment for ${familyName}.`
              : `Select which children to add to the ${enrollmentKind === 'RE_ENROLLMENT' ? 're-enrollment' : 'enrollment'}.`
            }
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4">
          {/* No children available - show message */}
          {availableChildren.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Children Available</h3>
              <p className="text-muted-foreground max-w-md">
                {hasExistingEnrollment
                  ? "All children in this family are already enrolled for this school year."
                  : "This family doesn't have any children yet. Please add children to the family first from the General tab."}
              </p>
            </div>
          ) : (
            <>
              {step === 0 && (
                <div className="grid gap-4 md:grid-cols-2">
                  <Card
                    className={`cursor-pointer transition-colors ${
                      enrollmentKind === 'NEW_ADMISSION'
                        ? 'border-primary bg-primary/5'
                        : 'hover:border-muted-foreground'
                    }`}
                    onClick={() => setEnrollmentKind('NEW_ADMISSION')}
                  >
                    <CardHeader>
                      <CardTitle className="text-lg">New Admission</CardTitle>
                      <CardDescription>
                        {checklistEnrollmentOn
                          ? CHECKLIST_KIND_WORDS.NEW_ADMISSION.description
                          : 'For families new to the school'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="text-sm text-muted-foreground list-disc list-inside">
                        {checklistEnrollmentOn ? (
                          CHECKLIST_KIND_WORDS.NEW_ADMISSION.steps.map((step) => <li key={step}>{step}</li>)
                        ) : (
                          <>
                            <li>Full application process</li>
                            <li>Admissions review required</li>
                          </>
                        )}
                      </ul>
                    </CardContent>
                  </Card>

                  <Card
                    className={`cursor-pointer transition-colors ${
                      enrollmentKind === 'RE_ENROLLMENT'
                        ? 'border-primary bg-primary/5'
                        : 'hover:border-muted-foreground'
                    }`}
                    onClick={() => setEnrollmentKind('RE_ENROLLMENT')}
                  >
                    <CardHeader>
                      <CardTitle className="text-lg">Re-Enrollment</CardTitle>
                      <CardDescription>
                        {checklistEnrollmentOn
                          ? CHECKLIST_KIND_WORDS.RE_ENROLLMENT.description
                          : 'For families returning to the school'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="text-sm text-muted-foreground list-disc list-inside">
                        {checklistEnrollmentOn ? (
                          CHECKLIST_KIND_WORDS.RE_ENROLLMENT.steps.map((step) => <li key={step}>{step}</li>)
                        ) : (
                          <>
                            <li>Streamlined confirmation</li>
                            <li>Existing records retained</li>
                          </>
                        )}
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              )}

              {step === 1 && (
                <SelectStudentsStep
                  students={studentsForStep}
                  selections={selections}
                  onSelectionsChange={setSelections}
                  gradeLevels={gradeLevels}
                  enrollmentKind={enrollmentKind}
                />
              )}
            </>
          )}
        </div>

        <DialogFooter className="flex justify-between items-center gap-2">
          <div className="flex gap-2">
            {availableChildren.length > 0 && step > 0 && !hasExistingEnrollment && (
              <Button variant="outline" onClick={handleBack} disabled={loading}>
                Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => handleClose(false)} disabled={loading}>
              {availableChildren.length === 0 ? 'Close' : 'Cancel'}
            </Button>
            {availableChildren.length > 0 && (
              step === 0 ? (
                <Button onClick={handleNext}>
                  Next
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={loading || !canProceed}>
                  {loading ? 'Adding...' : `Add ${selectedCount} Student${selectedCount !== 1 ? 's' : ''}`}
                </Button>
              )
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AddStudentsToFamilyWizard;
